import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Music2, Megaphone } from "lucide-react";
import api, { formatApiErrorDetail, isPlatinumRequired } from "../lib/api";
import { INSTRUMENTS, EVENT_TYPES } from "../lib/instruments";
import { localYmd } from "../lib/utils";
import { toast } from "sonner";

const GIG_TYPES = [...EVENT_TYPES.filter((e) => e.value !== "emergency_dep"), { value: "other", label: "Other" }];

export default function NewGig() {
  const navigate = useNavigate();
  const today = localYmd();
  const [form, setForm] = useState({
    title: "",
    description: "",
    event_type: "concert",
    date: today,
    start_time: "19:30",
    end_time: "21:30",
    venue: "",
    location: "",
    ensemble_name: "",
    repertoire: "",
    fee_info: "",
  });
  const [seats, setSeats] = useState([{ instrument: "French Horn", count_needed: 1, notes: "" }]);
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSeat = (i, k, v) => setSeats((arr) => arr.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const addSeat = () => setSeats((arr) => [...arr, { instrument: "", count_needed: 1, notes: "" }]);
  const removeSeat = (i) => setSeats((arr) => arr.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (seats.length === 0 || seats.some((s) => !s.instrument)) {
      toast.error("Add at least one seat with an instrument");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        event_type: form.event_type,
        date: form.date,
        start_at: new Date(`${form.date}T${form.start_time}:00`).toISOString(),
        end_at: new Date(`${form.date}T${form.end_time}:00`).toISOString(),
        venue: form.venue,
        location: form.location,
        ensemble_name: form.ensemble_name,
        repertoire: form.repertoire,
        fee_info: form.fee_info,
        seats: seats.map((s) => ({ instrument: s.instrument, count_needed: Number(s.count_needed) || 1, notes: s.notes || "" })),
      };
      const { data } = await api.post("/gigs", payload);
      toast.success("Gig posted");
      navigate(`/gigs/${data.id}`);
    } catch (err) {
      if (isPlatinumRequired(err)) {
        toast.error("Posting gigs is a Platinum feature. Redirecting to pricing…");
        setTimeout(() => navigate("/pricing"), 800);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="new-gig-page">
      <Link to="/gigs" className="btn-ghost text-sm w-fit"><ArrowLeft size={16} /> Back to gigs</Link>

      <div>
        <div className="label-overline inline-flex items-center gap-2"><Megaphone size={12} /> New gig</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Post a gig</h1>
        <p className="text-ink-600 text-sm mt-1">List one or more instrument seats. Musicians apply directly; you confirm.</p>
      </div>

      <form onSubmit={submit} className="card-base p-5 sm:p-6 space-y-4">
        <div>
          <label className="label-overline block mb-2">Title</label>
          <input required className="input-field" value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Spring Concert — Mahler 5" data-testid="gig-title-input" />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label-overline block mb-2">Type</label>
            <select className="input-field" value={form.event_type} onChange={(e) => setField("event_type", e.target.value)} data-testid="gig-type-select">
              {GIG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-overline block mb-2">Date</label>
            <input required type="date" min={new Date().toISOString().slice(0, 10)} className="input-field" value={form.date} onChange={(e) => setField("date", e.target.value)} data-testid="gig-date-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Start</label>
            <input required type="time" className="input-field" value={form.start_time} onChange={(e) => setField("start_time", e.target.value)} data-testid="gig-start-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">End</label>
            <input required type="time" className="input-field" value={form.end_time} onChange={(e) => setField("end_time", e.target.value)} data-testid="gig-end-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Venue</label>
            <input className="input-field" value={form.venue} onChange={(e) => setField("venue", e.target.value)} placeholder="Sheldonian Theatre" data-testid="gig-venue-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Location (for search)</label>
            <input className="input-field" value={form.location} onChange={(e) => setField("location", e.target.value)} placeholder="Oxford, UK" data-testid="gig-location-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Ensemble</label>
            <input className="input-field" value={form.ensemble_name} onChange={(e) => setField("ensemble_name", e.target.value)} placeholder="Oxford Sinfonia" data-testid="gig-ensemble-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Fee / expenses</label>
            <input className="input-field" value={form.fee_info} onChange={(e) => setField("fee_info", e.target.value)} placeholder="£80 + travel" data-testid="gig-fee-input" />
          </div>
        </div>

        <div>
          <label className="label-overline block mb-2">Repertoire</label>
          <input className="input-field" value={form.repertoire} onChange={(e) => setField("repertoire", e.target.value)} placeholder="Mahler 5, Britten Variations" data-testid="gig-rep-input" />
        </div>
        <div>
          <label className="label-overline block mb-2">Description / notes</label>
          <textarea rows={3} className="input-field" value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Two rehearsals + concert. Looking to fill out brass." data-testid="gig-description-input" />
        </div>

        <div className="border-t border-ivory-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="label-overline inline-flex items-center gap-2"><Music2 size={12} /> Seats needed</div>
            <button type="button" onClick={addSeat} className="btn-ghost text-xs" data-testid="add-seat-btn"><Plus size={14} /> Add seat</button>
          </div>
          <div className="space-y-3">
            {seats.map((s, i) => (
              <div key={i} className="rounded-xl bg-ivory-100/60 p-3 space-y-2" data-testid={`seat-row-${i}`}>
                <div className="grid grid-cols-12 gap-2">
                  <select className="input-field col-span-7" value={s.instrument} onChange={(e) => setSeat(i, "instrument", e.target.value)} required data-testid={`seat-instrument-${i}`}>
                    <option value="">Instrument</option>
                    {INSTRUMENTS.map((ins) => <option key={ins} value={ins}>{ins}</option>)}
                  </select>
                  <input type="number" min={1} max={20} className="input-field col-span-3" value={s.count_needed} onChange={(e) => setSeat(i, "count_needed", e.target.value)} data-testid={`seat-count-${i}`} />
                  <button type="button" onClick={() => removeSeat(i)} className="col-span-2 btn-ghost text-terracotta-600 justify-center" data-testid={`seat-remove-${i}`} disabled={seats.length === 1}><Trash2 size={16} /></button>
                </div>
                <input className="input-field" value={s.notes} onChange={(e) => setSeat(i, "notes", e.target.value)} placeholder="Notes (e.g. principal preferred)" data-testid={`seat-notes-${i}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2 flex justify-end">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="gig-submit-btn">{busy ? "Posting…" : "Post gig"}</button>
        </div>
      </form>
    </div>
  );
}
